import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  getGroupsForApplication,
  addIdentityMembershipsToGroup,
  deleteIdentityMembershipFromGroup,
  closeApprovalRequiredModal,
} from "../../actions";
import { sidebar_groups } from "../../config";
import { errorAction, types as at } from "@authzsvc/api-lib";
import { Modal, Typography, Alert, Card, Space, Tooltip, Button } from "antd";
import { DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { getActiveApplication, isLoading } from "../../selectors";
import {
  FullPageLoader,
  ContentTable,
} from "@authzsvc/common-react-components";
import GroupSearch from "../groups/GroupSearch";
import { showErrorNotification } from "../../utils/index";
import { types, successAction } from "@authzsvc/api-lib";

const { Text, Link } = Typography;

class GroupMemberships extends React.Component {
  static propTypes = {
    loadingGroups: PropTypes.bool.isRequired,
    loadingApp: PropTypes.bool,
    application: PropTypes.object.isRequired,
    groups: PropTypes.array,
    approvalRequiredModalOpen: PropTypes.bool,
    getGroupsForApplication: PropTypes.func.isRequired,
    addIdentityMembershipsToGroup: PropTypes.func.isRequired,
    closeApprovalRequiredModal: PropTypes.func.isRequired,
    deleteIdentityMembershipFromGroup: PropTypes.func.isRequired,
  };

  componentDidMount() {
    this.props.getGroupsForApplication(this.props.application);
  }

  handleAddGroup = async (result) => {
    const {
      application,
      addIdentityMembershipsToGroup,
      getGroupsForApplication,
    } = this.props;
    const groupId = result.group.id;

    try {
      if (!application.identityId) {
        showErrorNotification(
          "Error in the application",
          "Requested application could not be fetched. Please try again later.",
        );
        return;
      }

      const identityIds = [{ id: application.identityId }];

      const response = await addIdentityMembershipsToGroup(
        groupId,
        identityIds,
      );

      if (response.type === successAction(types.ADD_GROUP_MEMBER_IDENTITIES)) {
        getGroupsForApplication(application);
      } else {
        showErrorNotification(
          response.payload.title,
          response.payload.data.message,
          10,
        );
      }
    } catch (error) {
      console.error("Error resolving identity or adding membership: ", error);
      showErrorNotification(
        "Error resolving identity or adding membership",
        "Unable to process the request. Please try again later.",
        10,
      );
    }
  };

  render() {
    const {
      loadingGroups,
      loadingApp,
      application,
      groups = [],
      approvalRequiredModalOpen,
      closeApprovalRequiredModal,
      deleteIdentityMembershipFromGroup,
    } = this.props;

    const loading = loadingGroups || loadingApp;

    if (loading || !application.displayName) {
      return <FullPageLoader />;
    }

    const confirmDelete = (group) => {
      Modal.confirm({
        title: "Delete membership",
        icon: <ExclamationCircleOutlined />,
        content: `Are you sure you want to remove this application from "${group.displayName}"?`,
        okText: "Delete membership",
        okButtonProps: { danger: true },
        cancelText: "Cancel",
        onOk: async () => {
          const resp = await deleteIdentityMembershipFromGroup(
            group.id,
            application,
          );
          if (
            resp.type === errorAction(at.DELETE_IDENTITY_MEMBERSHIP_FROM_GROUP)
          ) {
            showErrorNotification(
              resp.payload.title,
              resp.payload.data.message,
            );
          }
        },
      });
    };

    const columns = [
      {
        title: "Group Name",
        dataIndex: "displayName",
        key: "displayName",
        render: (text, group) => (
          <a
            id="to-group-portal"
            target="_blank"
            rel="noopener noreferrer"
            href={`${sidebar_groups}/group/${group.groupIdentifier}/details`}
          >
            {text}
          </a>
        ),
      },
      {
        title: "Group Identifier",
        dataIndex: "groupIdentifier",
        key: "groupIdentifier",
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        render: (text, group) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <span>{text}</span>
            <Tooltip title="Unsubscribe">
              <Button
                id="unsubscribeButton"
                icon={<DeleteOutlined />}
                danger
                color="red"
                variant="filled"
                onClick={() => confirmDelete(group)}
              />
            </Tooltip>
          </div>
        ),
      },
    ];

    return (
      <>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          description={
            <>
              If your application needs special permissions, you can subscribe
              it to the relevant groups here. You must have permissions to
              manage the membership of the group.
              <br />
              Group memberships are relevant only for applications using
              application credentials to authenticate to other applications.
            </>
          }
        />

        <ContentTable
          columns={columns}
          dataSource={groups.map((g) => ({ ...g, key: g.id }))}
          rowKey="id"
          pagination={false}
          loading={loading}
          locale={{
            emptyText: "This Application is not a member of any group yet.",
          }}
          style={{ marginBottom: 16 }}
        />

        <Card type="inner" title="Subscribe this application to a Group">
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Text type="secondary">
              Please note that groups with a sync type set as{" "}
              <Text code>Replica</Text> are not displayed, as their membership
              must be edited via{" "}
              <Link
                href="https://e-groups.cern.ch/"
                target="_blank"
                rel="noopener noreferrer"
              >
                e-groups
              </Link>
              .
            </Text>
            <GroupSearch
              handleResultSelect={this.handleAddGroup}
              isEditingMembership={true}
              className="wider-search"
            />
          </Space>
        </Card>

        <Modal
          open={approvalRequiredModalOpen}
          onCancel={closeApprovalRequiredModal}
          onOk={closeApprovalRequiredModal}
          okText="OK"
          title="Approval required"
          closable
          centered
        >
          The owner of this Group has to approve your application&apos;s
          membership. An approval request has been created and sent.
        </Modal>
      </>
    );
  }
}

export default connect(
  (state) => ({
    active_application: getActiveApplication(state),
    loadingGroups: isLoading(state, "getGroupsForApplication"),
  }),
  {
    getGroupsForApplication,
    addIdentityMembershipsToGroup,
    deleteIdentityMembershipFromGroup,
    closeApprovalRequiredModal,
  },
)(GroupMemberships);
